import * as THREE from 'three';
import { emis, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { folk } from './corazon.ts';
import { tallerModel } from './taller.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * The eyes (docs/sendas/artifice.md, chapter 4): its people, painted miniatures.
 *
 * - Young Nadd (the memory, fifty-five years back): a dwarf in his thirties, both eyes, a black beard in braids with
 *   iron rings, the golem-maker's leather apron, a hammer at his belt; no flask yet.
 * - Bruni, his apprentice: young and quick, a short russet beard, a leather cap, a wedging maul.
 * - Grillo new: bright bronze, no green in its seams yet, its lamp new and red.
 * - The old golems from the forge, borrowed: the workshop's golem (models/taller.ts).
 * - Adela at fifteen: slight, smudged with stone dust, a pick on her shoulder.
 * - Grillo come back broken: lying on his side, a leg gone, his lamp cracked and out, his back dented.
 * - La Barrena: a nightmare of blue ice and old iron that put itself together out of the mine's broken drills. A body
 *   of ice ribs bound in rusted bands, a cold light in its chest; two arms that end in augers (they spin); a broken
 *   drill bit for a head with two pale eyes; two pillar legs of ice shod in iron. Its bones: body, head, arms, augers
 *   and legs, so it walks, lifts its augers (its warning), slashes, spins (anim/creatures/barrena.ts).
 *
 * Colors: slate, iron, bronze (never gold), the lamps the dragon's red.
 */

const LAMP_RED = '#ff4f3a';

/** Grillo, `new` (bright bronze, the memory) or `broken` (one leg gone, the lamp cracked and dark, lying down). */
const grilloLike = (o: BaseOpts | undefined, look: 'new' | 'broken'): Model => {
  const c = begin(o, 0.42, look === 'new' ? 881 : 882);
  const root = c.g;
  root.scale.setScalar(1.3);
  // Lying on his side, broken (the body turned on the ground).
  const g = look === 'broken' ? G(root, 0, 0.27, 0) : root;
  if (look === 'broken') g.rotation.set(0, 0, Math.PI / 2 - 0.15);
  const bronze = look === 'new' ? metal('#a06a3c', { roughness: 0.32, metalness: 0.75, flatShading: true }) : metal('#5a4e3e', { roughness: 0.6, flatShading: true });
  const band = look === 'new' ? metal('#c78a52', { roughness: 0.28, metalness: 0.8, flatShading: true }) : std('#5d8a7c', { roughness: 0.85, flatShading: true });
  const dark = metal('#2a2622', { roughness: 0.6 });
  const lamp = look === 'new' ? emis('#ffd0c4', LAMP_RED, 2.4) : std('#3a1814', { roughness: 0.3, metalness: 0.2 });
  const by = look === 'broken' ? -0.27 : 0;
  const body = M(new THREE.CylinderGeometry(0.27, 0.3, 0.46, 12), bronze, g);
  body.position.y = 0.52 + by;
  for (const y of [0.34, 0.52, 0.7]) ring(g, 0.302, 0.022, band, y + by, 4, 14);
  if (look === 'broken') {
    // Dents in his back: dark hollows pressed in.
    for (const [x, y] of [
      [0.1, 0.6],
      [-0.12, 0.46],
      [0.02, 0.38],
    ] as const) {
      const d = M(new THREE.SphereGeometry(0.08, 7, 5), std('#2a241e', { roughness: 0.9 }), g);
      d.position.set(x, y + by, -0.26);
      d.scale.set(1, 0.8, 0.35);
    }
  }
  // Legs: both new; broken, only the left (a stump of torn bronze on the right).
  for (const k of [-1, 1]) {
    if (look === 'broken' && k > 0) {
      const st = M(new THREE.CylinderGeometry(0.06, 0.05, 0.08, 6), dark, g);
      st.position.set(k * 0.14, 0.28 + by, 0);
      continue;
    }
    limb(g, V(k * 0.14, 0.32 + by, 0), V(k * 0.17, 0.08 + by, 0.03), 0.06, 0.07, dark, 6);
    M(new THREE.BoxGeometry(0.16, 0.07, 0.24), bronze, g).position.set(k * 0.17, 0.035 + by, 0.06);
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.07, 8, 6), band, g).position.set(k * 0.3, 0.66 + by, 0.02);
    limb(g, V(k * 0.31, 0.64 + by, 0.02), V(k * 0.36, 0.36 + by, 0.08), 0.04, 0.045, bronze, 6);
    limb(g, V(k * 0.36, 0.36 + by, 0.08), V(k * 0.33, 0.1 + by, 0.16), 0.035, 0.04, bronze, 6);
  }
  const head = G(g, 0, 0.78 + by, 0.02);
  M(new THREE.SphereGeometry(0.2, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), bronze, head);
  ring(head, 0.2, 0.02, band, 0.0, 4, 14);
  const rim = M(new THREE.TorusGeometry(0.085, 0.022, 6, 14), dark, head);
  rim.position.set(0, 0.07, 0.17);
  rim.rotation.x = -0.25;
  const eye = M(new THREE.SphereGeometry(0.068, 10, 8), lamp, head);
  eye.position.set(0, 0.07, 0.17);
  if (look === 'new') glowSprite(head, LAMP_RED, 0.6, V(0, 0.07, 0.24), 0.9);
  else {
    // The crack across the glass.
    const cr = M(new THREE.BoxGeometry(0.13, 0.008, 0.01), std('#d8d4d0', { roughness: 0.4 }), head);
    cr.position.set(0, 0.075, 0.235);
    cr.rotation.z = 0.6;
  }
  const ch = M(new THREE.CylinderGeometry(0.035, 0.045, 0.2, 7), dark, g);
  ch.position.set(0.08, 0.86 + by, -0.18);
  ch.rotation.x = -0.3;
  const tick = (n: number): void => {
    if (look === 'new') (eye.material as THREE.MeshStandardMaterial).emissiveIntensity = 2.1 + 0.4 * Math.sin(n * 1.9);
    if (look === 'new') head.rotation.y = Math.sin(n * 0.5) * 0.22;
  };
  return done(c, look === 'new' ? 1.3 : 0.8, tick);
};

const naddYoung = (o?: BaseOpts): Model => folk(o, 931, { coat: '#3c3f47', under: '#4a3e36', legs: '#2a2c31', skin: '#c49a84', hair: '#1d1a19', beard: '#1d1a19', apron: '#4a3a2e', tool: 'hammer', dwarf: true });
const bruniYoung = (o?: BaseOpts): Model => folk(o, 932, { coat: '#5a4639', under: '#3f444c', legs: '#2c2e33', skin: '#cfa38a', hair: '#7a4426', beard: '#8a4e2a', apron: '#5a4a3c', cap: '#3a2e26', tool: 'maul', dwarf: true });
const adelaGirl = (o?: BaseOpts): Model => {
  const m = folk(o, 933, { coat: '#5a5f66', under: '#6a6e74', legs: '#33363b', skin: '#c9a593', hair: '#3a2a22', apron: '#8a8780', tool: 'pick', bun: true });
  m.root.scale.multiplyScalar(0.86);
  return m;
};

/* ---------- La Barrena ---------- */

export const barrena = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 934);
  const g = c.g;
  const ice = std('#8fc4de', { roughness: 0.18, metalness: 0.1, flatShading: true, transparent: true, opacity: 0.92 });
  const iceD = std('#4f86a6', { roughness: 0.3, flatShading: true });
  const rust = metal('#6a4a3a', { roughness: 0.7, flatShading: true });
  const iron = metal('#4a5058', { roughness: 0.45, flatShading: true });
  const bright = metal('#9aa2ac', { roughness: 0.28, metalness: 0.8, flatShading: true });
  const cold = emis('#e6f8ff', '#7fe0ff', 2.4);
  const body = joint(g, V(0, 1.0, 0), 'body');
  const B = body.abs;
  // The chest: shards of ice in a cage of rusted bands, a cold light in it.
  const chest = M(new THREE.DodecahedronGeometry(0.5, 0), ice, B);
  chest.position.set(0, 1.35, 0);
  chest.scale.set(1.1, 1.25, 0.85);
  for (let i = 0; i < 5; i++) {
    const sh = M(new THREE.ConeGeometry(0.1, 0.5, 4), iceD, B);
    const a = (i / 5) * Math.PI * 2;
    sh.position.set(Math.cos(a) * 0.42, 1.55 + (i % 2) * 0.15, Math.sin(a) * 0.3 - 0.05);
    sh.rotation.set(Math.sin(a) * 0.6, 0, -Math.cos(a) * 0.7);
  }
  for (const y of [1.08, 1.38, 1.66]) {
    const b = ring(B, 0.5 - Math.abs(y - 1.38) * 0.3, 0.035, rust, y, 4, 12);
    b.scale.set(1.05, 1, 0.8);
  }
  const heart = M(new THREE.OctahedronGeometry(0.16, 0), cold, B);
  heart.position.set(0, 1.38, 0.18);
  glowSprite(B, '#7fe0ff', 1.1, V(0, 1.38, 0.4), 0.55);
  // The head: a broken drill bit, its flutes, two pale eyes.
  const head = joint(B, V(0, 1.92, 0.02), 'head');
  const bit = M(new THREE.ConeGeometry(0.2, 0.55, 6), bright, head.abs);
  bit.position.set(0, 2.12, 0.02);
  for (let i = 0; i < 3; i++) {
    const fl = M(new THREE.TorusGeometry(0.16 - i * 0.04, 0.02, 3, 10), iron, head.abs);
    fl.position.set(0, 1.98 + i * 0.12, 0.02);
    fl.rotation.x = Math.PI / 2 + 0.25;
  }
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.04, 6, 5), cold, head.abs).position.set(k * 0.08, 1.98, 0.19);
  const skel: Record<string, THREE.Object3D> = { body: body.p, head: head.p };
  // The arms: iron, ending in augers that spin.
  for (const k of [-1, 1]) {
    const sd = k < 0 ? 'L' : 'R';
    const sh = V(k * 0.56, 1.62, 0);
    const arm = joint(B, sh, `arm${sd}`);
    M(new THREE.SphereGeometry(0.17, 8, 6), rust, arm.abs).position.copy(sh);
    limb(arm.abs, sh, V(k * 0.78, 1.15, 0.2), 0.09, 0.08, iron, 7);
    const aug = joint(arm.abs, V(k * 0.8, 1.05, 0.25), `aug${sd}`);
    M(new THREE.CylinderGeometry(0.14, 0.14, 0.16, 8), rust, aug.abs).position.set(k * 0.8, 1.05, 0.25);
    const cone = M(new THREE.ConeGeometry(0.15, 0.85, 7), bright, aug.abs);
    cone.position.set(k * 0.8, 0.55, 0.25);
    cone.rotation.x = Math.PI;
    for (let i = 0; i < 4; i++) {
      const fl = M(new THREE.TorusGeometry(0.13 - i * 0.025, 0.018, 3, 9), iron, aug.abs);
      fl.position.set(k * 0.8, 0.88 - i * 0.18, 0.25);
      fl.rotation.set(Math.PI / 2 + 0.3, 0, i * 0.8);
    }
    Object.assign(skel, { [`arm${sd}`]: arm.p, [`aug${sd}`]: aug.p });
  }
  // The legs: pillars of ice shod in iron.
  for (const k of [-1, 1]) {
    const sd = k < 0 ? 'L' : 'R';
    const hip = V(k * 0.26, 0.98, 0);
    const leg = joint(g, hip, `leg${sd}`);
    const pillar = M(new THREE.CylinderGeometry(0.17, 0.13, 0.86, 6), ice, leg.abs);
    pillar.position.set(k * 0.27, 0.55, 0);
    M(new THREE.BoxGeometry(0.32, 0.16, 0.42), iron, leg.abs).position.set(k * 0.28, 0.08, 0.05);
    ring(leg.abs, 0.17, 0.03, rust, 0.78, 3, 10).position.x = k * 0.27;
    Object.assign(skel, { [`leg${sd}`]: leg.p });
  }
  const tick = (n: number): void => {
    (heart.material as THREE.MeshStandardMaterial).emissiveIntensity = 2.0 + 0.6 * Math.sin(n * 2.6);
  };
  return done(c, 2.4, tick, undefined, bonesOf(c, skel));
};

/** The eyes' models by name (null: none of them). */
export const ojosModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'naddYoung':
      return naddYoung(o);
    case 'bruniYoung':
      return bruniYoung(o);
    case 'grilloNew':
      return grilloLike(o, 'new');
    case 'grilloBroken':
      return grilloLike(o, 'broken');
    case 'oldGolem':
      return tallerModel('workGolem', o);
    case 'adelaGirl':
      return adelaGirl(o);
    case 'barrena':
      return barrena(o);
    default:
      return null;
  }
};
